import React, { useEffect, useState } from "react";
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Typography,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";

/* ------------------------- Importaciones internas ------------------------- */
import { api } from "../../../../services/api";
import { esEmailValido } from "../../../../core/utils/validate";
import { GENERAL, ADMINISTRADOR } from "../../../../core/utils/roles.constants";
import BotonGenerico from "../../../../components/Button/Button";
import DialogFooter from "../../../../components/DialogFooter/DialogActions";
import { useLoading } from "../../../../contexts/LoadingContext";
import { useAuth } from "../../../../contexts/AuthContext";

/* --------------------------------- Estilos -------------------------------- */
const useStyles = makeStyles((theme) => ({
  dialogContent: {
    padding: theme.spacing(4),
    width: "900px",
    boxSizing: "border-box",
  },
  formRow: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  titleText: {
    textAlign: "center",
    marginBottom: theme.spacing(3),
  },
  closeButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
}));

const CrearConfiguracion = ({
  open,
  onClose,
  tipo,
  onSuccess,
  editando,
  sectores,
  rubros,
  listaUsuarios,
}) => {
  const classes = useStyles();
  const { show, hide } = useLoading();
  const { login, getUser } = useAuth();
  const user = getUser();

  const MAX_PASSWORD_LENGTH = 15;

  const [nombre, setNombre] = useState("");
  const [mostrarContraseña, setMostrarContraseña] = useState(false);

  const formDefaultValuesItem = {
    rubro: "",
    codigoItem: "",
    nombre: "",
    precioDefault: "",
    cantidad: "",
    descripcion: "",
  };

  const formDefaultValues = {
    nombre: "",
    apellido: "",
    email: "",
    contraseña: "",
    fechaRegistro: new Date().toISOString().split("T")[0],
    sectores: [],
    es_admin: 2,
  };

  const [formData, setFormData] = useState(formDefaultValues);
  const [formDataItem, setFormDataItem] = useState(formDefaultValuesItem);
  const [errors, setErrors] = useState({});
  const [checkAdministrador, setCheckAdministrador] = useState(false);

  const isUsuario = tipo === "usuarios";
  const isItem = tipo === "items";

  const correoYaExistente = (email) => {
    if (!Array.isArray(listaUsuarios)) return false; // 🔒 evita el error
    return listaUsuarios.some(
      (usuario) => usuario.email.toLowerCase() === email.toLowerCase()
    );
  };

  const validarDatos = () => {
    const newErrors = {};

    if (isUsuario) {
      if (!formData.nombre.trim())
        newErrors.nombre = "El nombre es obligatorio.";
      if (!formData.apellido.trim())
        newErrors.apellido = "El apellido es obligatorio.";

      if (!formData.email.trim()) {
        newErrors.email = "El email es obligatorio.";
      } else if (!esEmailValido(formData.email)) {
        newErrors.email = "El email no es válido.";
      } else if (
        correoYaExistente(formData.email) &&
        formData.email !== editando?.email
      ) {
        newErrors.email = "Este email ya está en uso.";
      }

      if (!formData.contraseña.trim()) {
        newErrors.contraseña = "La contraseña es obligatoria.";
      } else if (formData.contraseña.length < 6) {
        newErrors.contraseña =
          "La contraseña debe tener al menos 6 caracteres.";
      } else if (formData.contraseña.length > MAX_PASSWORD_LENGTH) {
        newErrors.contraseña =
          `La contraseña no debe superar los ${MAX_PASSWORD_LENGTH} caracteres.`;
      }

      if (!formData.es_admin) newErrors.es_admin = "El rol es obligatorio.";

      if (!formData.sectores || formData.sectores.length === 0)
        newErrors.sectores = "Debe seleccionar al menos un sector.";
    } else if (isItem) {
      if (!formDataItem.codigoItem.trim())
        newErrors.codigoItem = "El código es obligatorio.";
      if (!formDataItem.nombre.trim())
        newErrors.nombre = "El nombre es obligatorio.";
      if (!formDataItem.rubro) newErrors.rubro = "El rubro es obligatorio.";
    } else {
      if (!nombre.trim()) newErrors.nombre = "El nombre es obligatorio.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleGuardar = async () => {
    const validacion = validarDatos();
    if (!validacion) return;

    var textoGuardar;

    try {
      let response;

      if (isUsuario) {
        textoGuardar = "Guardando usuario...";
        const body = {
          Nombre: formData.nombre,
          Apellido: formData.apellido,
          email: formData.email,
          Contraseña: formData.contraseña,
          fechaRegistro: formData.fechaRegistro,
          IdSector: formData.sectores[0] || null,
          foto: "",
          oculto: 0,
          es_admin: formData.es_admin,
        };

        let userId;
        if (editando?.IdUsuario) {
          response = await api.usuarios.update(editando.IdUsuario, body);
          userId = editando.IdUsuario;
        } else {
          response = await api.usuarios.create(body);
          userId = response?.data?.IdUsuario || response?.IdUsuario;
        }

        // Guardar sectores en tabla puente
        if (userId && formData.sectores.length > 0) {
          await api.usuarios.setSectores(userId, formData.sectores);
        }

        // Inyectar sectores en la respuesta para que onSuccess actualice el estado local
        const userData = response?.data || response;
        if (userData && isUsuario) {
          userData.sectores = formData.sectores;
          userData.IdSector = formData.sectores[0] || null;
        }
        response = { data: userData };
      } else if (isItem) {
        textoGuardar = "Guardando ítem...";
        const body = {
          idRubro: formDataItem.rubro,
          codigoItem: formDataItem.codigoItem,
          nombre: formDataItem.nombre,
          precioDefault: Number(formDataItem.precioDefault),
          cantidad: Number(formDataItem.cantidad),
          descripcion: formDataItem.descripcion,
          oculto: 0,
        };

        if (editando?.IdItems) {
          response = await api.items.update(editando.IdItems, body);
          login(response.user, response.token);
        } else {
          response = await api.items.create(body);
        }
      } else {
        const body = { Nombre: nombre, oculto: 0 };

        switch (tipo) {
          case "rubro":
            textoGuardar = "Guardando rubro...";
            if (editando?.IdRubro) {
              response = await api.rubros.update(editando.IdRubro, body);
            } else {
              response = await api.rubros.create(body);
            }
            break;
          case "sector":
            textoGuardar = "Guardando sector...";
            if (editando?.IdSector) {
              response = await api.sectores.update(editando.IdSector, body);
            } else {
              response = await api.sectores.create(body);
            }
            break;
          case "tipoproducto":
            textoGuardar = "Guardando tipo de producto...";
            if (editando?.IdTipoProducto) {
              response = await api.tiposProducto.update(
                editando.IdTipoProducto,
                body
              );
            } else {
              response = await api.tiposProducto.create(body);
            }
            break;
          case "tipocomprobante":
            textoGuardar = "Guardando tipo de comprobante...";
            if (editando?.idtipo_comprobante) {
              response = await api.tipoComprobante.update(
                editando.idtipo_comprobante,
                body
              );
            } else {
              response = await api.tipoComprobante.create(body);
            }
            break;
          case "criteriosevaluacion":
            textoGuardar = "Guardando criterio de evaluación...";
            if (editando?.idcriterios_evaluacion) {
              response = await api.criterios.update(
                editando.idcriterios_evaluacion,
                body
              );
            } else {
              response = await api.criterios.create(body);
            }
            break;
          default:
            return;
        }
      }
      if (response) {
        show(textoGuardar);
        const resData = response.data || response;
        if (resData) {
          onSuccess(resData);
        } else {
          onSuccess(response);
        }

        // Si se editó el propio usuario, actualizar localStorage con los nuevos sectores
        if (isUsuario) {
          const currentUser = JSON.parse(localStorage.getItem("userData") || "null");
          const editedId = editando?.IdUsuario || resData?.IdUsuario || resData?.id;
          if (currentUser && String(currentUser.IdUsuario || currentUser.id) === String(editedId)) {
            const updatedUser = { ...currentUser, sectores: formData.sectores, IdSector: formData.sectores[0] || currentUser.IdSector };
            localStorage.setItem("userData", JSON.stringify(updatedUser));
          }
        }

        cerrarModal();
      }
    } catch (error) {
      console.error("Error al guardar:", error);
    } finally {
      hide();
    }
  };

  const cerrarModal = () => {
    setNombre("");
    setFormData(formDefaultValues);
    setFormDataItem(formDefaultValuesItem);
    setErrors({});
    setMostrarContraseña(false);
    onClose();
  };

  useEffect(() => {
    if (open) {
      if (editando) {
        if (isUsuario) {
          setFormData({
            nombre: editando.Nombre || "",
            apellido: editando.Apellido || "",
            email: editando.email || "",
            contraseña: editando.Contraseña || "",
            sectores: editando.sectores || (editando.IdSector ? [editando.IdSector] : []),
            es_admin: editando.es_admin || GENERAL,
          });
        } else if (isItem) {
          setFormDataItem({
            codigoItem: editando.codigoItem || "",
            nombre: editando.nombre || "",
            rubro: editando.IdRubro || "",
            precioDefault: editando.precioDefault || "",
            descripcion: editando.descripcion || "",
          });
        } else {
          setNombre(editando.Nombre || "");
        }
        setCheckAdministrador(user.es_admin === ADMINISTRADOR);
      } else {
        setNombre("");
        setFormData(formDefaultValues);
        setFormDataItem(formDefaultValuesItem);
        setCheckAdministrador(true);
      }
    }
  }, [open, editando, tipo]);

  const nombreTipo =
    tipo === "usuarios"
      ? "Usuario"
      : tipo === "rubro"
        ? "Rubro"
        : tipo === "sector"
          ? "Sector"
          : tipo === "tipoproducto"
            ? "Tipo de productos"
            : tipo === "items"
              ? "Items"
              : tipo === "tipocomprobante"
                ? "Tipos de comprobantes"
                : tipo === "criteriosevaluacion"
                  ? "Evaluaciones de criterios"
                  : "Desconocido";

  return (
    <Dialog open={open} onClose={cerrarModal} maxWidth="xl" fullWidth PaperProps={{ style: { width: "900px", maxWidth: "900px" } }}>
      <DialogTitle disableTypography>
        <Typography variant="h6" className={classes.titleText}>
          {editando ? "Editando" : "Nuevo"} {nombreTipo}
        </Typography>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={cerrarModal}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {isUsuario ? (
          <>
            <div className={classes.formRow}>
              <div style={{ flex: 1 }}>
                <Typography variant="subtitle1">Nombre</Typography>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={formData.nombre}
                  onChange={(e) =>
                    setFormData({ ...formData, nombre: e.target.value })
                  }
                  disabled={!checkAdministrador}
                  error={!!errors.nombre || undefined}
                />
                {errors.nombre && (
                  <Typography color="error" variant="body2">
                    {errors.nombre}
                  </Typography>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <Typography variant="subtitle1">Apellido</Typography>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={formData.apellido}
                  onChange={(e) =>
                    setFormData({ ...formData, apellido: e.target.value })
                  }
                  disabled={!checkAdministrador}
                  error={!!errors.apellido || undefined}
                />
                {errors.apellido && (
                  <Typography color="error" variant="body2">
                    {errors.apellido}
                  </Typography>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <Typography variant="subtitle1">Contraseña</Typography>
                <TextField
                  variant="outlined"
                  type={mostrarContraseña ? "text" : "password"}
                  fullWidth
                  value={formData.contraseña}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      contraseña: e.target.value.slice(0, MAX_PASSWORD_LENGTH),
                    })
                  }
                  disabled={!checkAdministrador}
                  inputProps={{ maxLength: MAX_PASSWORD_LENGTH }}
                  error={!!errors.contraseña || undefined}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() => setMostrarContraseña(!mostrarContraseña)}
                        edge="end"
                      >
                        {mostrarContraseña ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    ),
                  }}
                />
                <Typography variant="body2" color="textSecondary">
                  Máximo {MAX_PASSWORD_LENGTH} caracteres.
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {formData.contraseña.length}/{MAX_PASSWORD_LENGTH}
                </Typography>
                {errors.contraseña && (
                  <Typography color="error" variant="body2">
                    {errors.contraseña}
                  </Typography>
                )}
              </div>
            </div>
            <div className={classes.formRow}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1">Sectores</Typography>

                <Select
                  variant="outlined"
                  fullWidth
                  multiple
                  value={formData.sectores || []}
                  onChange={(e) =>
                    setFormData({ ...formData, sectores: e.target.value })
                  }
                  renderValue={(selected) => (
                    <Typography
                      noWrap
                      style={{
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {selected
                        .map((id) => sectores.find((s) => s.IdSector === id)?.Nombre || id)
                        .join(", ")}
                    </Typography>
                  )}
                  disabled={!checkAdministrador}
                  error={!!errors.sectores || undefined}
                >
                  {sectores && sectores.length > 0 ? (
                    sectores.map((s) => (
                      <MenuItem key={s.IdSector} value={s.IdSector}>
                        <Checkbox
                          checked={(formData.sectores || []).indexOf(s.IdSector) > -1}
                        />
                        <ListItemText primary={s.Nombre} />
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled value="">
                      No hay sectores disponibles
                    </MenuItem>
                  )}
                </Select>

                {errors.sectores && (
                  <Typography color="error" variant="body2">
                    {errors.sectores}
                  </Typography>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <Typography variant="subtitle1">Rol</Typography>
                <Select
                  variant="outlined"
                  fullWidth
                  value={formData.es_admin ?? GENERAL} // valor por defecto General
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      es_admin: Number(e.target.value),
                    })
                  }
                  disabled={!checkAdministrador}
                  error={!!errors.es_admin}
                >
                  <MenuItem value={1}>Administrador</MenuItem>
                  <MenuItem value={5}>Administración</MenuItem>
                  <MenuItem value={2}>Director</MenuItem>
                  <MenuItem value={4}>Transferencia</MenuItem>
                  <MenuItem value={3}>General</MenuItem>
                  {/* {sectores && Object.keys(sectores).length > 0 ? (
                    sectores.map((s) => (
                      <MenuItem key={s.IdSector} value={s.IdSector}>
                        {s.Nombre}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled value="">
                      No hay sectores disponibles
                    </MenuItem>
                  )} */}
                </Select>
                {errors.sector && (
                  <Typography color="error" variant="body2">
                    {errors.sector}
                  </Typography>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <Typography variant="subtitle1">Email</Typography>
                <TextField
                  variant="outlined"
                  type="email"
                  fullWidth
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  disabled={!checkAdministrador}
                  error={!!errors.email || undefined}
                />
                {errors.email && (
                  <Typography color="error" variant="body2">
                    {errors.email}
                  </Typography>
                )}
              </div>
            </div>
          </>
        ) : isItem ? (
          <>
            <div className={classes.formRow}>
              <div style={{ flex: 1 }}>
                <Typography variant="subtitle1">Código</Typography>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={formDataItem.codigoItem}
                  onChange={(e) =>
                    setFormDataItem({
                      ...formDataItem,
                      codigoItem: e.target.value,
                    })
                  }
                  disabled={!checkAdministrador}
                  error={!!errors.codigoItem || undefined}
                />
                {errors.codigoItem && (
                  <Typography color="error" variant="body2">
                    {errors.codigoItem}
                  </Typography>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <Typography variant="subtitle1">Nombre</Typography>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={formDataItem.nombre}
                  onChange={(e) =>
                    setFormDataItem({ ...formDataItem, nombre: e.target.value })
                  }
                  disabled={!checkAdministrador}
                  error={!!errors.nombre || undefined}
                />
                {errors.nombre && (
                  <Typography color="error" variant="body2">
                    {errors.nombre}
                  </Typography>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <Typography variant="subtitle1">Rubro</Typography>
                <Select
                  variant="outlined"
                  fullWidth
                  value={formDataItem.rubro}
                  onChange={(e) =>
                    setFormDataItem({
                      ...formDataItem,
                      rubro: Number(e.target.value),
                    })
                  }
                  disabled={!checkAdministrador}
                  error={!!errors.rubro || undefined}
                >
                  {rubros && Object.keys(rubros).length > 0 ? (
                    rubros.map((r) => (
                      <MenuItem key={r.IdRubro} value={r.IdRubro}>
                        {r.Nombre}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled value="">
                      No hay rubros disponibles
                    </MenuItem>
                  )}
                </Select>
                {errors.rubro && (
                  <Typography color="error" variant="body2">
                    {errors.rubro}
                  </Typography>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <Typography variant="subtitle1">Precio por defecto</Typography>
                <TextField
                  variant="outlined"
                  type="number"
                  fullWidth
                  value={formDataItem.precioDefault}
                  onChange={(e) => {
                    if (Number(e.target.value) <= 0) {
                      setFormDataItem({ ...formDataItem, precioDefault: 0 });
                      return;
                    }
                    setFormDataItem({
                      ...formDataItem,
                      precioDefault: e.target.value,
                    });
                  }}
                  onKeyDown={(e) => {
                    if (["e", "E", "+", "-", "."].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  disabled={!checkAdministrador}
                  error={!!errors.precioDefault || undefined}
                />
                {errors.precioDefault && (
                  <Typography color="error" variant="body2">
                    {errors.precioDefault}
                  </Typography>
                )}
              </div>
            </div>
            <div className={classes.formRow}>
              <div style={{ flex: 1 }}>
                <Typography variant="subtitle1">Descripción</Typography>
                <TextField
                  variant="outlined"
                  fullWidth
                  rows={3}
                  multiline
                  value={formDataItem.descripcion}
                  onChange={(e) =>
                    setFormDataItem({
                      ...formDataItem,
                      descripcion: e.target.value,
                    })
                  }
                  disabled={!checkAdministrador}
                  error={!!errors.descripcion || undefined}
                />
                {errors.descripcion && (
                  <Typography color="error" variant="body2">
                    {errors.descripcion}
                  </Typography>
                )}
              </div>
            </div>
          </>
        ) : (
          // <>
          <div style={{ flex: 1 }}>
            <Typography variant="subtitle1">{`Nombre del ${nombreTipo}`}</Typography>
            <TextField
              variant="outlined"
              autoFocus
              fullWidth
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              disabled={!checkAdministrador}
              error={!!errors.nombre || undefined}
            />
            {errors.nombre && (
              <Typography color="error" variant="body2">
                {errors.nombre}
              </Typography>
            )}
          </div>
        )}
      </DialogContent>

      {checkAdministrador && (
        <DialogFooter>
          <BotonGenerico
            tipo={"guardar"}
            color="primary"
            onClick={handleGuardar}
            text="Guardar"
          />
          <BotonGenerico
            color="secondary"
            onClick={cerrarModal}
            text="Cancelar"
          />
        </DialogFooter>
      )}
    </Dialog>
  );
};

export default CrearConfiguracion;
